/**
 * Single source of truth for datetime formatting across order surfaces.
 *
 * Every formatter accepts an optional `timeZoneId` (IANA id, e.g.
 * "Europe/London", "Asia/Karachi"). When callers pass the shop's / provider's
 * own tz the wall-clock reads identically across all surfaces AND matches
 * what the operator actually sees on their dashboard — not the viewer's
 * browser tz, and not a hardcoded UK zone. When omitted, we fall back to
 * `FALLBACK_TIMEZONE_ID` (Europe/London) so legacy call-sites keep working.
 *
 * Callers pinning a shop / provider tz should pass e.g.
 * `formatDateTime(row.CreatedAt, getTimeZoneIdForRegion(order.shop.regionCode))`.
 */
import { FALLBACK_TIMEZONE_ID } from '@/config/regionTimezones'

/**
 * Full absolute datetime with year — used in tables where the row could be
 * years old and the year matters (audit table, payment history rows).
 * Example: "06 Aug 2026, 14:23:45".
 */
export function formatDateTime(dateInput?: string | Date | null, timeZoneId?: string | null): string {
  if (!dateInput) return ''
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleString('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: timeZoneId ?? FALLBACK_TIMEZONE_ID,
  })
}

/**
 * Short absolute datetime WITHOUT year — used in compact chips (order
 * timeline, "created" line under order number). The year is omitted because
 * these chips are read in-context ("this order I'm looking at right now")
 * and the extra digits crowd the pill.
 * Example: "06 Aug 14:23:45".
 */
export function formatDateTimeShort(dateInput?: string | Date | null, timeZoneId?: string | null): string {
  if (!dateInput) return ''
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  const tz = timeZoneId ?? FALLBACK_TIMEZONE_ID
  const day = d.toLocaleString('en-GB', { day: '2-digit', timeZone: tz })
  const month = d.toLocaleString('en-GB', { month: 'short', timeZone: tz })
  const time = d.toLocaleString('en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    timeZone: tz,
  })
  return `${day} ${month} ${time}`
}

/**
 * Relative-time formatter — "just now", "3m ago", "2h ago", "5d ago",
 * otherwise falls back to the short absolute form. Used in the payment
 * timeline where "3 minutes ago" carries more meaning than a wall-clock time.
 */
export function formatRelativeTime(dateInput?: string | Date | null, timeZoneId?: string | null): string {
  if (!dateInput) return ''
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  const diffMs = Date.now() - d.getTime()
  if (diffMs < 0) return formatDateTimeShort(d, timeZoneId)
  const seconds = Math.floor(diffMs / 1000)
  if (seconds < 45) return 'just now'
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  const days = Math.floor(hours / 24)
  if (days < 7) return `${days}d ago`
  return formatDateTimeShort(d, timeZoneId)
}

/**
 * Generic timezone-aware date formatter — thin wrapper over
 * `toLocaleDateString('en-GB', ...)` that always threads `timeZone` through
 * the options so wall-clock reads match the operator's dashboard.
 *
 * Use for any call-site that previously did
 *   `new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', ... })`
 * — pass the exact same options object plus the shop / user timezone:
 *   `formatDate(iso, { day: 'numeric', month: 'short' }, shopStore.selectShopTimezone)`.
 *
 * Preserves the existing en-GB output shape while making tz explicit.
 */
export function formatDate(
  dateInput?: string | Date | null,
  options: Intl.DateTimeFormatOptions = {},
  timeZoneId?: string | null,
): string {
  if (!dateInput) return ''
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-GB', {
    ...options,
    timeZone: timeZoneId ?? FALLBACK_TIMEZONE_ID,
  })
}

/**
 * Generic timezone-aware time formatter — sibling to `formatDate` for
 * `.toLocaleTimeString('en-GB', ...)` call-sites. Same tz-threading rules.
 */
export function formatTime(
  dateInput?: string | Date | null,
  options: Intl.DateTimeFormatOptions = { hour: '2-digit', minute: '2-digit' },
  timeZoneId?: string | null,
): string {
  if (!dateInput) return ''
  const d = dateInput instanceof Date ? dateInput : new Date(dateInput)
  if (isNaN(d.getTime())) return ''
  return d.toLocaleTimeString('en-GB', {
    ...options,
    timeZone: timeZoneId ?? FALLBACK_TIMEZONE_ID,
  })
}

/**
 * Vue composable wrapper — makes the helpers ergonomic to consume from
 * templates via `const { formatDateTime } = useFormatDate()`. All functions
 * are pure so they work equally well as bare imports.
 */
export function useFormatDate() {
  return {
    formatDateTime,
    formatDateTimeShort,
    formatRelativeTime,
    formatDate,
    formatTime,
  }
}
